import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";

import { db } from "../../firebase";

function formatRowCreatedAt(row) {
  const ts = row.createdAt;
  if (ts && typeof ts.toDate === "function") {
    try {
      return ts.toDate().toLocaleString();
    } catch {
      return "—";
    }
  }
  const ms = Number(row.createdAtMs);
  if (Number.isFinite(ms) && ms > 0) {
    return new Date(ms).toLocaleString();
  }
  return "—";
}

function escapeCsvCell(value) {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function metaSummary(meta) {
  if (meta == null || meta === "") return "—";
  if (typeof meta !== "object") {
    const t = String(meta);
    return t.length > 160 ? `${t.slice(0, 160)}…` : t;
  }
  try {
    const keys = Object.keys(meta);
    if (keys.length === 0) return "—";
    const parts = keys.slice(0, 8).map((k) => {
      const v = meta[k];
      const vv =
        v != null && typeof v === "object"
          ? JSON.stringify(v)
          : String(v ?? "");
      return `${k}: ${vv.length > 80 ? `${vv.slice(0, 80)}…` : vv}`;
    });
    return parts.join(" · ");
  } catch {
    return "—";
  }
}

export default function AuditLogsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState(null);

  const [filterAction, setFilterAction] = useState("");
  const [filterActorUid, setFilterActorUid] = useState("");
  const [filterTargetUid, setFilterTargetUid] = useState("");
  const [filterTargetType, setFilterTargetType] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  useEffect(() => {
    const q = query(
      collection(db, "adminAuditLogs"),
      orderBy("createdAtMs", "desc"),
      limit(100)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setLoadError(null);
        setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error("AuditLogsPage snapshot error:", err);
        setLoadError(err?.message || "Failed to load audit logs.");
      }
    );

    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const actionNeedle = filterAction.trim().toLowerCase();
    const actorNeedle = filterActorUid.trim().toLowerCase();
    const targetUidNeedle = filterTargetUid.trim().toLowerCase();
    const targetTypeNeedle = filterTargetType.trim().toLowerCase();

    let fromMs = null;
    let toMs = null;
    if (filterDateFrom) {
      const d = new Date(filterDateFrom);
      if (!Number.isNaN(d.getTime())) fromMs = d.getTime();
    }
    if (filterDateTo) {
      const d = new Date(filterDateTo);
      if (!Number.isNaN(d.getTime())) {
        toMs = d.getTime() + 24 * 60 * 60 * 1000 - 1;
      }
    }

    return rows.filter((row) => {
      if (actionNeedle) {
        const a = String(row.action || "").toLowerCase();
        if (!a.includes(actionNeedle)) return false;
      }
      if (actorNeedle) {
        const a = String(row.actorUid || "").toLowerCase();
        if (!a.includes(actorNeedle)) return false;
      }
      if (targetUidNeedle) {
        const t = String(row.targetUid || "").toLowerCase();
        if (!t.includes(targetUidNeedle)) return false;
      }
      if (targetTypeNeedle) {
        const t = String(row.targetType || "").toLowerCase();
        if (!t.includes(targetTypeNeedle)) return false;
      }
      const ms = Number(row.createdAtMs);
      if (fromMs != null && Number.isFinite(ms) && ms < fromMs) {
        return false;
      }
      if (toMs != null && Number.isFinite(ms) && ms > toMs) {
        return false;
      }
      return true;
    });
  }, [
    rows,
    filterAction,
    filterActorUid,
    filterTargetUid,
    filterTargetType,
    filterDateFrom,
    filterDateTo,
  ]);

  function exportCsv() {
    const headers = [
      "createdAt",
      "actorUid",
      "actorRole",
      "action",
      "targetType",
      "targetId",
      "targetUid",
      "metaSummary",
    ];
    const lines = [headers.join(",")];
    for (const row of filtered) {
      const created =
        row.createdAt && typeof row.createdAt.toDate === "function"
          ? row.createdAt.toDate().toISOString()
          : Number.isFinite(Number(row.createdAtMs))
          ? new Date(Number(row.createdAtMs)).toISOString()
          : "";
      const cells = [
        created,
        row.actorUid,
        row.actorRole,
        row.action,
        row.targetType,
        row.targetId,
        row.targetUid,
        metaSummary(row.meta),
      ].map(escapeCsvCell);
      lines.push(cells.join(","));
    }
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `admin-audit-logs-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">{t("admin.audit_logs.title", { defaultValue: "Audit logs" })}</h1>
        <p className="admin1-dashboard__subtitle">
          Last 100 server-recorded admin actions (read-only). Filter client-side
          on this page.
        </p>
      </header>

      {loadError ? (
        <div className="admin1-state-card">{loadError}</div>
      ) : null}

      <div className="admin1-audit__toolbar">
        <button
          type="button"
          className="admin1-audit__btn"
          onClick={exportCsv}
          disabled={filtered.length === 0}
        >
          Export CSV
        </button>
        <span className="admin1-audit__count">
          Showing {filtered.length} of {rows.length} loaded
        </span>
      </div>

      <section className="admin1-audit__filters">
        <label className="admin1-audit__field">
          <span>{t("admin.audit_logs.action_contains", { defaultValue: "Action contains" })}</span>
          <input
            type="text"
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            placeholder={t("admin.audit_logs.action_placeholder", { defaultValue: "e.g. withdraw_" })}
            autoComplete="off"
          />
        </label>
        <label className="admin1-audit__field">
          <span>{t("admin.audit_logs.actor_uid_contains", { defaultValue: "Actor UID contains" })}</span>
          <input
            type="text"
            value={filterActorUid}
            onChange={(e) => setFilterActorUid(e.target.value)}
            autoComplete="off"
          />
        </label>
        <label className="admin1-audit__field">
          <span>{t("admin.audit_logs.target_uid_contains", { defaultValue: "Target UID contains" })}</span>
          <input
            type="text"
            value={filterTargetUid}
            onChange={(e) => setFilterTargetUid(e.target.value)}
            autoComplete="off"
          />
        </label>
        <label className="admin1-audit__field">
          <span>{t("admin.audit_logs.target_type_contains", { defaultValue: "Target type contains" })}</span>
          <input
            type="text"
            value={filterTargetType}
            onChange={(e) => setFilterTargetType(e.target.value)}
            placeholder={t("admin.audit_logs.target_type_placeholder", { defaultValue: "user, withdrawal..." })}
            autoComplete="off"
          />
        </label>
        <label className="admin1-audit__field">
          <span>{t("admin.audit_logs.from_date", { defaultValue: "From date" })}</span>
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
          />
        </label>
        <label className="admin1-audit__field">
          <span>{t("admin.audit_logs.to_date", { defaultValue: "To date" })}</span>
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
          />
        </label>
      </section>

      <div className="admin1-audit__table-wrap">
        <table className="admin1-audit__table">
          <thead>
            <tr>
              <th>{t("admin.audit_logs.created", { defaultValue: "Created" })}</th>
              <th>{t("admin.audit_logs.actor_uid", { defaultValue: "Actor UID" })}</th>
              <th>{t("common.action", { defaultValue: "Action" })}</th>
              <th>{t("admin.audit_logs.target_type", { defaultValue: "Target type" })}</th>
              <th>{t("admin.audit_logs.target_id", { defaultValue: "Target ID" })}</th>
              <th>{t("admin.audit_logs.target_uid", { defaultValue: "Target UID" })}</th>
              <th>{t("admin.audit_logs.meta", { defaultValue: "Meta" })}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id}>
                <td className="admin1-audit__mono-wrap">
                  {formatRowCreatedAt(row)}
                </td>
                <td className="admin1-audit__mono-wrap">
                  {row.actorUid || "—"}
                </td>
                <td>{row.action || "—"}</td>
                <td>{row.targetType || "—"}</td>
                <td className="admin1-audit__mono-wrap">
                  {row.targetId || "—"}
                </td>
                <td className="admin1-audit__mono-wrap">
                  {row.targetUid || "—"}
                </td>
                <td className="admin1-audit__meta">{metaSummary(row.meta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && !loadError ? (
        <div className="admin1-state-card">{t("admin.audit_logs.empty", { defaultValue: "No audit entries yet." })}</div>
      ) : null}
    </div>
  );
}
